/**
 * PTW draft mapper — applies an AI prefill draft (or a "start from this"
 * similar record) onto PTWCreate's formData.
 *
 * Allowlist mirrors the backend PTWDraft whitelist ∩ the fields that actually
 * exist on the PTW create form. Anything else in the draft is dropped with a
 * warning (draft-shape drift degrades to partial fill, never breakage).
 *
 * Never applied here (by omission from FIELD_SPECS):
 *   site_id/site_name (SiteContext-owned), applicant fields, workers,
 *   gas_test_readings, safety_checklist (template-owned — filling
 *   permit_types triggers the existing template effect), status,
 *   permit_number, linked_hira_id (goes through the linkHIRA UX), file URLs.
 */

import {
  applyDraftWithSpecs,
  cleanPhraseList,
  coerceDateTimeLocal,
  coerceEnum,
} from './mergeUtils';

export const PTW_PERMIT_TYPES = [
  'Hot Work', 'Confined Space', 'Work at Height', 'Electrical Work',
  'Excavation', 'Lifting Operations', 'Chemical Handling',
  'Isolation Permit', 'Radiography', 'General Maintenance',
];

const TYPE_ALIASES = {
  'working at height': 'Work at Height',
  'electrical': 'Electrical Work',
  'hotwork': 'Hot Work',
  'confined space entry': 'Confined Space',
  'lifting': 'Lifting Operations',
  'maintenance': 'General Maintenance',
};

function futureDateTime(value) {
  const dt = coerceDateTimeLocal(value);
  if (!dt) return null;
  if (dt.slice(0, 10) < new Date().toISOString().slice(0, 10)) return null;
  return dt;
}

/**
 * The Department control on this form is a select of department NAMES
 * (`value={d.name}`), unlike CAPA's, which stores department_id. So validate
 * the AI's name against the tenant's own master list and return the canonical
 * spelling. Fails closed — an unknown department is left blank rather than
 * invented.
 */
function resolveDepartmentName(value, ctx) {
  const departments = (ctx && ctx.departments) || [];
  const hit = departments.find(
    (d) => (d.name || '').toLowerCase() === String(value).trim().toLowerCase()
  );
  return hit ? hit.name : null;
}

const FIELD_SPECS = {
  // The backend emits ONE permit_type string, but this form's real field is
  // `permit_types` (an array driven by the checkbox list). Writing the singular
  // key only would land on the vestigial legacy <select>: no box ticked, the
  // step-1 gate unsatisfied, and — because the template effect keys on
  // `permit_types` — no safety checklist loaded either.
  permit_type: {
    label: 'Permit Type',
    formKey: 'permit_types',
    coerce: (v) => {
      const t = coerceEnum(v, PTW_PERMIT_TYPES, TYPE_ALIASES);
      return t ? [t] : null;
    },
  },
  work_description: { label: 'Work Description', coerce: (v) => String(v).slice(0, 5000) },
  location: { label: 'Location', coerce: (v) => String(v).slice(0, 500) },
  department: { label: 'Department', coerce: resolveDepartmentName },
  contractor_company: { label: 'Contractor Company', coerce: (v) => String(v).slice(0, 300) },
  work_start_date: { label: 'Work Start Date', coerce: futureDateTime },
  work_end_date: { label: 'Work End Date', coerce: futureDateTime },
  hazards_identified: { label: 'Hazards Identified', coerce: (v) => cleanPhraseList(v) },
  control_measures: { label: 'Control Measures', coerce: (v) => cleanPhraseList(v) },
  emergency_contact_name: { label: 'Emergency Contact Name', coerce: (v) => String(v).slice(0, 200) },
  emergency_contact_number: { label: 'Emergency Contact Number', coerce: (v) => String(v).slice(0, 50) },
  rescue_plan_description: { label: 'Rescue Plan', coerce: (v) => String(v).slice(0, 2000) },
};

/**
 * applyDraft(draft, currentFormData, ctx = { departments: [{id, name}] }) →
 *   { formData, applied, skipped, warnings }
 *
 * Pure; fill-empty-only. Also enforces cross-field rule end > start, keeps the
 * permit-type companions in step with `permit_types`, and sets
 * rescue_plan_available when a rescue plan description was applied.
 */
export function applyDraft(draft, currentFormData, ctx = {}) {
  const result = applyDraftWithSpecs(draft || {}, currentFormData, FIELD_SPECS, ctx);

  // Cross-field: end must be after start (whichever source each came from).
  const { formData } = result;

  // Mirror the checkbox handler's companions so an AI-selected type behaves
  // exactly like a hand-ticked one: the legacy singular field and the
  // combined-permit numbering type both follow permit_types.
  if (result.applied.some((a) => a.path === 'permit_types')) {
    const types = formData.permit_types || [];
    formData.permit_type = types[0] || '';
    formData.numbering_type = types.includes(currentFormData.numbering_type)
      ? currentFormData.numbering_type
      : (types[0] || '');
  }
  if (formData.work_start_date && formData.work_end_date &&
      formData.work_end_date <= formData.work_start_date) {
    const idx = result.applied.findIndex((a) => a.path === 'work_end_date');
    if (idx !== -1) {
      formData.work_end_date = currentFormData.work_end_date || '';
      result.applied.splice(idx, 1);
      result.skipped.push({ path: 'work_end_date', label: 'Work End Date', reason: 'invalid-value' });
      result.warnings.push('Work End Date was not after the start date — left blank');
    }
  }

  if (result.applied.some((a) => a.path === 'rescue_plan_description') &&
      !currentFormData.rescue_plan_available) {
    formData.rescue_plan_available = true;
  }

  return result;
}

/** Map a similar-record row (GET /api/assistant/similar/ptw) into a draft
 *  for the same applyDraft path ("start from this one"). Dates are NOT
 *  copied — an old permit's dates are never valid for new work. */
export function draftFromSimilarRecord(record) {
  if (!record) return {};
  return {
    permit_type: record.permit_type,
    work_description: record.work_description,
    location: record.location,
    contractor_company: record.contractor_company,
    hazards_identified: record.hazards_identified || [],
    control_measures: record.control_measures || [],
    emergency_contact_name: record.emergency_contact_name,
    emergency_contact_number: record.emergency_contact_number,
    rescue_plan_description: record.rescue_plan_description,
  };
}
